import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Heading,
} from '@chakra-ui/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { FinancialCovenantsCalculation } from './utils';
import { axisTickFormatter } from 'packages/common-frontend/components/LineChartWithFormatters';
import { numberToDollarFormattedRounded } from 'packages/common-frontend/utils';

const covenantLineColor = '#6E9EFA';
const limitLineColor = 'red';

const CovenantTooltip: React.FC<{
  limitValue: number;
  valueFormatter: (value: any) => string;
  active?: boolean;
  payload?: any;
  label?: string;
}> = ({ limitValue, valueFormatter, active, payload, label }) => {
  if (active && payload && payload.length && label) {
    return (
      <Box
        backgroundColor="white"
        border="1px solid #e3e3e3"
        padding="5px 10px"
        color="gray.800"
      >
        <Box fontWeight="bold">
          {DateTime.fromISO(label).toFormat('MMMM dd, yyyy')}
        </Box>
        <Box color={covenantLineColor}>
          {_.startCase(payload[0].name)}: {valueFormatter(payload[0].value)}
        </Box>
        <Box color={limitLineColor}>Limit: {valueFormatter(limitValue)}</Box>
      </Box>
    );
  }

  return null;
};

const FinancialCovenantChart: React.FC<{
  covenantKey: CreditAgreement.Config.FinancialCovenant.FinancialCovenantKey;
  data: FinancialCovenantsCalculation[];
  valueFormatter: (value: any) => string;
  yaxisTickFormatter: (value: any) => string;
}> = ({ covenantKey, data, valueFormatter, yaxisTickFormatter }) => {
  const chartData = useMemo(
    () =>
      _.map(data, ({ reportingDate, value }) => ({
        xAxisValue: reportingDate,
        [covenantKey]: value,
      })),
    [covenantKey, data],
  );

  if (data.length === 0) {
    return (
      <Box>
        <Heading as="h3" fontSize="xl" mb="30px">
          {commonUtils.toTitleCase(covenantKey)}
        </Heading>
        <Alert status="info" mt="20px">
          <AlertIcon />
          <AlertDescription>No covenant data available</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box height="100%">
      <Heading as="h3" fontSize="xl" mb="30px" color="gray.800">
        {commonUtils.toTitleCase(covenantKey)}
      </Heading>
      <ResponsiveContainer height={400} width="90%" debounce={100}>
        <LineChart
          margin={{
            top: 10,
            right: 0,
            left: 10,
            bottom: 10,
          }}
          data={chartData}
        >
          <CartesianGrid vertical={false} />
          <Tooltip
            content={
              <CovenantTooltip
                limitValue={data[0].limitValue}
                valueFormatter={valueFormatter}
              />
            }
          />
          <XAxis
            xAxisId={0}
            dataKey="xAxisValue"
            label={{
              value: 'Date',
              position: 'insideBottom',
              style: { textAnchor: 'middle', fontWeight: 'bold' },
            }}
            axisLine={false}
            tickLine
            tickFormatter={(value: Util.FullDateStr) =>
              DateTime.fromISO(value).toFormat('MMM yyyy')
            }
            height={70}
            padding={{ left: 40, right: 40 }}
            domain={['auto', 'auto']}
          />
          <YAxis
            label={{
              value: commonUtils.toTitleCase(covenantKey),
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontWeight: 'bold' },
            }}
            width={80}
            tickFormatter={yaxisTickFormatter}
            tickLine={false}
            axisLine={false}
            name={commonUtils.toSentenceCase(covenantKey)}
          />
          <ReferenceLine
            ifOverflow="extendDomain"
            stroke={limitLineColor}
            y={data[0].limitValue}
            strokeWidth={2}
          />
          <Line
            type="monotone"
            connectNulls
            dataKey={covenantKey}
            stroke={covenantLineColor}
            strokeWidth={2}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const CurrencyLimitGraph: React.FC<{
  covenantKey: CreditAgreement.Config.FinancialCovenant.FinancialCovenantKey;
  data: FinancialCovenantsCalculation[];
}> = ({ covenantKey, data }) => {
  return (
    <FinancialCovenantChart
      covenantKey={covenantKey}
      data={data}
      valueFormatter={(value: any) =>
        numberToDollarFormattedRounded(value) || `${value}`
      }
      yaxisTickFormatter={axisTickFormatter.currency}
    />
  );
};

export const RatioLimitGraph: React.FC<{
  covenantKey: CreditAgreement.Config.FinancialCovenant.FinancialCovenantKey;
  data: FinancialCovenantsCalculation[];
}> = ({ covenantKey, data }) => {
  return (
    <FinancialCovenantChart
      covenantKey={covenantKey}
      data={data}
      valueFormatter={(value: any) => `${value.toFixed(2)}x`}
      yaxisTickFormatter={axisTickFormatter.number}
    />
  );
};

export const PercentageLimitGraph: React.FC<{
  covenantKey: CreditAgreement.Config.FinancialCovenant.FinancialCovenantKey;
  data: FinancialCovenantsCalculation[];
}> = ({ covenantKey, data }) => {
  return (
    <FinancialCovenantChart
      covenantKey={covenantKey}
      data={data}
      valueFormatter={(value: any) => axisTickFormatter.percentage(value)}
      yaxisTickFormatter={axisTickFormatter.percentage}
    />
  );
};
